import { type FC, HTMLAttributes, PropsWithChildren } from 'react';

import { Box } from '../Box';
import { type TTextSize, Text } from '../Typography';
import { tagChipVariantStyles } from './TagChip.styles';
import { TagChipSize, TagChipVariant } from './TagChip.types';

export type TagChipProps = HTMLAttributes<HTMLSpanElement> & {
  /**
   * Visual variant of the chip
   */
  variant?: TagChipVariant;
  /**
   * Size of the chip. Analogous to Button sizes. Defaults to `md`.
   */
  size?: TagChipSize;
  /**
   * Additional CSS class names
   */
  className?: string;
};

const sizeStyles: Record<
  TagChipSize,
  { px: string; py: string; textSize: TTextSize }
> = {
  sm: { px: 'var(--spacing-3)', py: 'var(--spacing-2)', textSize: 'xs' },
  md: { px: 'var(--spacing-4)', py: 'var(--spacing-3)', textSize: 'sm' },
  lg: { px: 'var(--spacing-5)', py: 'var(--spacing-4)', textSize: 'md' },
};

const TagChip: FC<PropsWithChildren<TagChipProps>> = ({
  variant = 'primary',
  size = 'md',
  className,
  children,
  ...rest
}) => {
  const { px, py, textSize } = sizeStyles[size];

  return (
    <Box
      display="inline-flex"
      alignItems="center"
      borderRadius="var(--spacing-6)"
      px={px}
      py={py}
      css={tagChipVariantStyles[variant]}
      className={className}
      {...rest}
    >
      <Text as="span" size={textSize} fontWeight="normal">
        {children}
      </Text>
    </Box>
  );
};

export default TagChip;
